import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  RefreshCw,
  Users,
  Calendar,
  Settings as SettingsIcon,
  Search,
  Trash2,
  Download,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { adminApi, publicApi } from "@/lib/api";

const STATUS_LABELS = {
  new: "新登記",
  contacted: "已聯絡",
  attended: "已出席",
  purchased: "已購買",
};
const STATUS_COLORS = {
  new: "bg-slate-100 text-slate-800 border-slate-200",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  attended: "bg-emerald-50 text-emerald-700 border-emerald-200",
  purchased: "bg-amber-50 text-amber-800 border-amber-200",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(true);

  const [registrations, setRegistrations] = useState([]);
  const [search, setSearch] = useState("");
  const [filterIndustry, setFilterIndustry] = useState("all");
  const [filterBottleneck, setFilterBottleneck] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [dropdowns, setDropdowns] = useState({ industries: [], bottlenecks: [] });

  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [settingsForm, setSettingsForm] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) {
      navigate("/admin/login", { replace: true });
      return;
    }
    (async () => {
      try {
        await adminApi.me();
        setAuthed(true);
        await Promise.all([
          refreshRegistrations(),
          refreshSettings(),
          refreshStats(),
          publicApi.getDropdowns().then(setDropdowns),
        ]);
      } catch (err) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login", { replace: true });
      } finally {
        setLoading(false);
      }
      // eslint-disable-next-line
    })();
  }, []);

  const refreshRegistrations = async (q = {}) => {
    const params = { ...q };
    if (search) params.q = search;
    if (filterIndustry !== "all") params.industry = filterIndustry;
    if (filterBottleneck !== "all") params.bottleneck = filterBottleneck;
    if (filterStatus !== "all") params.status_filter = filterStatus;
    const data = await adminApi.listRegistrations(params);
    setRegistrations(data.items || []);
  };

  const refreshSettings = async () => {
    const s = await adminApi.getSettings();
    setSettings(s);
    setSettingsForm({
      webinar_date: s.webinar_date,
      webinar_time: s.webinar_time,
      duration_minutes: s.duration_minutes,
      total_seats: s.total_seats,
      seats_taken: s.seats_taken,
      original_price_hkd: s.original_price_hkd,
      show_original_price: s.show_original_price,
      whatsapp_redirect_url: s.whatsapp_redirect_url || "",
    });
  };

  const refreshStats = async () => {
    const st = await adminApi.stats();
    setStats(st);
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login", { replace: true });
  };

  const handleUpdateStatus = async (id, status) => {
    try {
      await adminApi.updateStatus(id, status);
      toast.success("狀態已更新");
      await refreshRegistrations();
    } catch {
      toast.error("更新失敗");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("確認刪除此報名紀錄？名額將釋放一個。")) return;
    try {
      await adminApi.deleteRegistration(id);
      toast.success("已刪除");
      await Promise.all([refreshRegistrations(), refreshSettings(), refreshStats()]);
    } catch {
      toast.error("刪除失敗");
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true);
      await adminApi.updateSettings({
        webinar_date: settingsForm.webinar_date,
        webinar_time: settingsForm.webinar_time,
        duration_minutes: Number(settingsForm.duration_minutes) || 120,
        total_seats: Number(settingsForm.total_seats) || 100,
        seats_taken: Number(settingsForm.seats_taken) || 0,
        original_price_hkd: Number(settingsForm.original_price_hkd) || 1980,
        show_original_price: Boolean(settingsForm.show_original_price),
        whatsapp_redirect_url: (settingsForm.whatsapp_redirect_url || "").trim(),
      });
      toast.success("設定已更新");
      await refreshSettings();
      setSettingsDialogOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "更新失敗");
    } finally {
      setSavingSettings(false);
    }
  };

  const exportCsv = () => {
    if (registrations.length === 0) {
      toast.error("目前沒有可匯出的資料");
      return;
    }
    const headers = ["姓名", "WhatsApp", "Email", "行業", "最大瓶頸", "狀態", "報名時間"];
    const rows = registrations.map((r) => [
      r.name,
      r.whatsapp,
      r.email,
      r.industry,
      r.bottleneck,
      STATUS_LABELS[r.status] || r.status,
      r.created_at,
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((x) => `"${String(x ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `webinar-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const remaining = useMemo(() => {
    if (!settings) return "—";
    return Math.max(0, settings.total_seats - settings.seats_taken);
  }, [settings]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(var(--background))]">
        <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  if (!authed) return null;

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[hsl(var(--card))] border-b border-[hsl(var(--border))]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="font-serif-tc text-base font-bold">Webinar 管理後台</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                await Promise.all([refreshRegistrations(), refreshSettings(), refreshStats()]);
                toast.success("已重新整理");
              }}
              className="rounded-lg"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="w-4 h-4 mr-1.5" /> 重新整理
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="rounded-lg"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4 mr-1.5" /> 登出
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Stats cards */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={<Users className="w-4 h-4" />} label="總報名人數" value={stats?.total_registrations ?? 0} />
          <StatCard icon={<Users className="w-4 h-4" />} label="名額剩餘" value={remaining} sub={`總名額 ${settings?.total_seats ?? "—"}`} />
          <StatCard
            icon={<Calendar className="w-4 h-4" />}
            label="活動日期"
            value={settings?.webinar_date ?? "—"}
            sub={`${settings?.webinar_time ?? "—"} 香港時間`}
          />
          <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="admin-open-settings"
                className="text-left rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4 hover:shadow-[0_14px_40px_rgba(14,27,42,0.08)] hover:border-[rgba(176,141,87,0.4)] transition-[box-shadow,border-color]"
              >
                <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
                  <SettingsIcon className="w-4 h-4" /> Webinar 設定
                </div>
                <div className="mt-2 text-lg font-serif-tc font-bold text-[hsl(var(--foreground))]">
                  修改時間 / 名額 / 原價
                </div>
                <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">點擊編輯</div>
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Webinar 設定</DialogTitle>
                <DialogDescription>更新後前台會即時反映。</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">活動日期</Label>
                    <Input
                      type="date"
                      data-testid="settings-webinar-date"
                      value={settingsForm.webinar_date || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, webinar_date: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">開始時間（HH:MM）</Label>
                    <Input
                      type="time"
                      data-testid="settings-webinar-time"
                      value={settingsForm.webinar_time || ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, webinar_time: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">總名額</Label>
                    <Input
                      type="number"
                      data-testid="settings-total-seats"
                      value={settingsForm.total_seats ?? ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, total_seats: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">已佔用名額</Label>
                    <Input
                      type="number"
                      data-testid="settings-seats-taken"
                      value={settingsForm.seats_taken ?? ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, seats_taken: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">時長（分鐘）</Label>
                    <Input
                      type="number"
                      value={settingsForm.duration_minutes ?? ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, duration_minutes: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">原價 HKD</Label>
                    <Input
                      type="number"
                      value={settingsForm.original_price_hkd ?? ""}
                      onChange={(e) => setSettingsForm({ ...settingsForm, original_price_hkd: e.target.value })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="show-price"
                    type="checkbox"
                    checked={!!settingsForm.show_original_price}
                    onChange={(e) => setSettingsForm({ ...settingsForm, show_original_price: e.target.checked })}
                  />
                  <Label htmlFor="show-price" className="text-sm">前台顯示原價劃線</Label>
                </div>
                <div>
                  <Label className="text-xs">WhatsApp 社群跳轉連結（報名成功後自動開啟）</Label>
                  <Input
                    type="url"
                    placeholder="https://chat.whatsapp.com/XXXXX"
                    data-testid="settings-wa-url"
                    value={settingsForm.whatsapp_redirect_url ?? ""}
                    onChange={(e) => setSettingsForm({ ...settingsForm, whatsapp_redirect_url: e.target.value })}
                    className="mt-1.5"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">用戶點擊「鎖定席位」成功後，會自動在新分頁開啟此連結</p>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSettingsDialogOpen(false)}>取消</Button>
                <Button onClick={handleSaveSettings} disabled={savingSettings} data-testid="settings-save-button">
                  {savingSettings && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}儲存設定
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </section>

        {/* Filters */}
        <section className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
            <div className="md:col-span-2 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" />
              <Input
                placeholder="搜尋姓名 / Email / WhatsApp"
                data-testid="admin-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && refreshRegistrations()}
                className="pl-9 h-10 rounded-xl"
              />
            </div>
            <Select value={filterIndustry} onValueChange={setFilterIndustry}>
              <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="全部行業" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部行業</SelectItem>
                {(dropdowns?.industries || []).map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterBottleneck} onValueChange={setFilterBottleneck}>
              <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="全部瓶頸" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部瓶頸</SelectItem>
                {(dropdowns?.bottlenecks || []).map((x) => (<SelectItem key={x} value={x}>{x}</SelectItem>))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="h-10 rounded-xl"><SelectValue placeholder="全部狀態" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部狀態</SelectItem>
                {Object.entries(STATUS_LABELS).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              共 <span className="font-semibold text-[hsl(var(--foreground))]">{registrations.length}</span> 筆結果
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => refreshRegistrations()} data-testid="admin-apply-filters" className="rounded-lg">
                套用篩選
              </Button>
              <Button variant="outline" size="sm" onClick={exportCsv} data-testid="admin-export-csv" className="rounded-lg">
                <Download className="w-4 h-4 mr-1.5" /> 匯出 CSV
              </Button>
            </div>
          </div>
        </section>

        {/* Registrations table */}
        <section className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="whitespace-nowrap">姓名</TableHead>
                  <TableHead className="whitespace-nowrap">WhatsApp</TableHead>
                  <TableHead className="whitespace-nowrap">Email</TableHead>
                  <TableHead className="whitespace-nowrap">行業</TableHead>
                  <TableHead className="whitespace-nowrap">瓶頸</TableHead>
                  <TableHead className="whitespace-nowrap">狀態</TableHead>
                  <TableHead className="whitespace-nowrap">報名時間</TableHead>
                  <TableHead className="whitespace-nowrap text-right">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {registrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-[hsl(var(--muted-foreground))]">
                      尚無符合條件的報名紀錄
                    </TableCell>
                  </TableRow>
                ) : (
                  registrations.map((r) => (
                    <TableRow key={r.id} data-testid={`registration-row-${r.id}`}>
                      <TableCell className="font-medium">{r.name}</TableCell>
                      <TableCell className="font-num">{r.whatsapp}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell className="text-sm">{r.industry}</TableCell>
                      <TableCell className="text-sm">{r.bottleneck}</TableCell>
                      <TableCell>
                        <Select value={r.status || "new"} onValueChange={(v) => handleUpdateStatus(r.id, v)}>
                          <SelectTrigger className={`h-8 w-28 rounded-lg text-xs ${STATUS_COLORS[r.status] || STATUS_COLORS.new}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(STATUS_LABELS).map(([k, v]) => (
                              <SelectItem key={k} value={k}>{v}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-[hsl(var(--muted-foreground))] whitespace-nowrap">
                        {r.created_at?.slice(0, 16).replace("T", " ")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(r.id)}
                          className="h-8 text-[hsl(var(--destructive))] hover:text-[hsl(var(--destructive))] hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </section>
      </main>
    </div>
  );
}

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="rounded-2xl bg-[hsl(var(--card))] border border-[hsl(var(--border))] p-4">
      <div className="flex items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
        {icon} {label}
      </div>
      <div className="mt-2 stat-num text-2xl sm:text-3xl text-[hsl(var(--foreground))]">{value}</div>
      {sub && <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">{sub}</div>}
    </div>
  );
}
